import React from "react";
import { Text, makeStyles, tokens } from "@fluentui/react-components";
import { DrayageQuote } from "../../../../types/drayage";
import { formatToCurrency } from "../../../../utils/currency";
import { formatMiles } from "../../../../utils/miles";
import RouteConnector from "./RouteConnector/RouteConnector";

interface RouteCardProps {
  quote: DrayageQuote;
  isActive: boolean;
  onQuoteSelect: (quote: DrayageQuote) => void;
}

const useStyles = makeStyles({
  container: {
    width: "100%",
    height: "max-content",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    padding: tokens.spacingVerticalS,
  },
  activeContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: "none",
  },
  routeInfo: {
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  costInfo: {
    paddingTop: tokens.spacingVerticalS,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    width: "100%",
    marginBottom: tokens.spacingVerticalXS,
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: "0 0 80px",
    maxWidth: "80px",
  },
  alignEnd: {
    width: "80px",
    textAlign: "end",
  },
  grayedOut: {
    color: tokens.colorNeutralForeground3,
  },
});

const RouteCard: React.FC<RouteCardProps> = ({
  quote,
  isActive,
  onQuoteSelect,
}) => {
  const styles = useStyles();

  const handleQuoteSelect = () => {
    onQuoteSelect(quote);
  };

  return (
    <div
      className={`${styles.container} ${isActive ? styles.activeContainer : ""}`}
      onClick={handleQuoteSelect}
    >
      <div className={styles.routeInfo}>
        <RouteConnector
          left={quote.pickupLocation}
          right={quote.deliveryLocation}
        />
      </div>

      <div className={styles.costInfo}>
        <div className={styles.row}>
          <Text className={styles.label}>Linehaul:</Text>
          <Text className={styles.alignEnd}>
            {formatToCurrency(quote.linehaulPrice)}
          </Text>
          <Text>{formatMiles(quote.roundtripMiles).formattedValue} miles</Text>
        </div>

        <div className={`${styles.row} ${styles.grayedOut}`}>
          <Text className={styles.label}>FSC: </Text>
          <Text className={styles.alignEnd}>
            {formatToCurrency(quote.fuelSurchargePrice)}
          </Text>
        </div>

        <div className={styles.row}>
          <Text className={styles.label}>Total:</Text>
          <Text className={styles.alignEnd}>
            {formatToCurrency(quote.quotePrice)}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default RouteCard;
